import React, { useState, useEffect, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import StripePaymentForm from "../components/StripePaymentForm";
import { toast } from "react-toastify";
import * as api from "../utils/api";

import { getEnvVar } from "../utils/env";
import { usePageTitle } from "../hooks/usePageTitle";
import { FiClipboard, FiFileText, FiCreditCard } from "react-icons/fi";

// Initialize Stripe with your publishable key
const STRIPE_KEY = getEnvVar(
	"REACT_APP_STRIPE_PUBLISHABLE_KEY",
	"pk_test_your_default_key"
);
const stripePromise = loadStripe(STRIPE_KEY);

interface TicketSelection {
	ticketId: number;
	quantity: number;
}

interface LocationState {
	tickets: TicketSelection[];
}

interface TicketType {
	id: number;
	name: string;
	price: number;
	is_free: boolean;
	description?: string;
	has_limit?: boolean;
	quantity?: number | null;
	sold_tickets?: number;
}

interface Event {
	id: number;
	external_id: string;
	title: string;
	date: string;
	venue_name: string;
	ticket_types: TicketType[];
	slug: string;
	cautions?: {
		id: number;
		content: string;
		position?: number;
	}[];
}

interface OrderResponse {
	success: boolean;
	order_id: number;
	client_secret?: string;
	is_free: boolean;
	amount: number;
	error?: string;
}

const CheckoutPage: React.FC = () => {
	const { eventExternalId } = useParams<{ eventExternalId: string }>();
	const location = useLocation();
	const navigate = useNavigate();
	const { user } = useAuth();
	const [step, setStep] = useState(1);
	const [isProcessing, setIsProcessing] = useState(false);
	const [orderId, setOrderId] = useState<number | null>(null);
	const [clientSecret, setClientSecret] = useState<string | null>(null);
	const [event, setEvent] = useState<Event | null>(null);
	const [totalAmount, setTotalAmount] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const cautionList = useMemo(() => {
		if (!event?.cautions) {
			return [];
		}
		return [...event.cautions]
			.filter((caution) => (caution.content || "").trim().length > 0)
			.sort(
				(a, b) =>
					(a.position ?? event.cautions?.indexOf(a) ?? 0) -
					(b.position ?? event.cautions?.indexOf(b) ?? 0)
			);
	}, [event]);

	const pageTitle = event?.title
		? `${event.title} - チケット購入`
		: "チケット購入";
	usePageTitle(pageTitle, { fallback: "チケット購入" });

	// Detect dark mode for Stripe Elements
	const isDarkMode =
		typeof window !== "undefined" &&
		window.matchMedia &&
		window.matchMedia("(prefers-color-scheme: dark)").matches;

	// Get state from location or redirect back to event page
	const state = location.state as LocationState;
	const initialEventPagePath = eventExternalId
		? `/events/${eventExternalId}`
		: "/events";
	const eventPagePath = event?.slug
		? `/events/${event.slug}`
		: initialEventPagePath;

	useEffect(() => {
		if (!state?.tickets || !eventExternalId) {
			navigate(initialEventPagePath);
			return;
		}

		const fetchEventData = async () => {
			setIsLoading(true);
			try {
				// Get event details using identifier (slug or ULID)
				interface ApiResponse {
					event: Event;
				}

				const response = await api.get<ApiResponse>(
					`/events/${eventExternalId}`
				);

				// Extract event data
				const eventData = response.event;

				if (!eventData) {
					throw new Error("イベント情報が取得できません");
				}

				// Ensure ticket_types exists
				if (!eventData.ticket_types?.length) {
					throw new Error("チケット情報が見つかりません");
				}

				setEvent(eventData);

				// Calculate total amount from selected tickets
				const selectedTickets = state.tickets.map((selection) => {
					const ticket = eventData.ticket_types.find(
						(t: TicketType) => t.id === selection.ticketId
					);

					if (!ticket) {
						throw new Error(
							`チケットID ${selection.ticketId} が見つかりません`
						);
					}

					return {
						...selection,
						ticket,
					};
				});

				const calculatedTotal = selectedTickets.reduce(
					(sum, { quantity, ticket }) => {
						if (ticket.is_free) return sum;
						return sum + Math.round(ticket.price) * quantity;
					},
					0
				);

				setTotalAmount(calculatedTotal);
				setIsLoading(false);
			} catch (error) {
				let errorMessage = "イベント情報の取得に失敗しました。";

				if (error instanceof Error) {
					errorMessage = error.message;
				} else if (typeof error === "string") {
					errorMessage = error;
				}

				setError(errorMessage);
				toast.error(errorMessage);
				setIsLoading(false);
			}
			};

			fetchEventData();
	}, [eventExternalId, navigate, state, initialEventPagePath]);

	const createOrder = async () => {
		setIsProcessing(true);
		try {
			if (!state?.tickets) {
				toast.error("注文情報が無効です。もう一度やり直してください。");
				navigate(eventPagePath);
				return;
			}

			// Make sure user is authenticated before proceeding
			if (!user) {
				toast.error("ログインが必要です");
				return;
			}

			// Make sure we have the event data
			if (!event) {
				toast.error("イベント情報が取得できません");
				return;
			}

			const payload = {
				event_external_id: event.external_id,
				user_id: user.id,
				customer_email: user.email,
				customer_name: user.name
					? `${user.name.lastName} ${user.name.firstName}`
					: undefined,
				tickets: state.tickets.map((item) => ({
					ticket_type_id: item.ticketId,
					quantity: item.quantity,
				})),
			};

			const response = await api.post<OrderResponse>(
				"/payments/stripe/create-order",
				payload
			);

			if (response.success) {
				// Handle free orders directly
				if (response.is_free) {
					toast.success("無料チケットの予約が完了しました！");
					navigate("/mypage");
					return;
				}

				// For paid orders, set up the payment form
				setOrderId(response.order_id);
				setClientSecret(response.client_secret || null);
				setStep(3); // Move to payment step
			} else {
				throw new Error(response.error || "注文の作成に失敗しました");
			}
		} catch (error) {
			let errorMessage = "注文の作成に失敗しました。もう一度お試しください。";

			if (error instanceof Error) {
				errorMessage = error.message;
			} else if (typeof error === "object" && error !== null) {
				// Handle API error response
				const apiError = error as any;
				if (apiError.response?.data?.message) {
					errorMessage = apiError.response.data.message;
				} else if (apiError.response?.data?.error) {
					errorMessage = apiError.response.data.error;
				}
			}

			toast.error(errorMessage);
		} finally {
			setIsProcessing(false);
		}
	};

	const moveToPaymentStatusPage = (targetOrderId: number) => {
		navigate(`/payment-success?order_id=${targetOrderId}`);
	};

	const handlePaymentError = (errorMessage: string) => {
		toast.error(errorMessage);
		// Stay on payment page to let the user try again
	};

	if (isLoading) {
		return (
			<div className="max-w-4xl mx-auto py-10 text-center">
				<p className="text-gray-600 dark:text-gray-300">
					読み込み中...
				</p>
			</div>
		);
	}

	if (error || !event) {
		return (
			<div className="max-w-4xl mx-auto py-10 text-center">
				<p className="text-red-600 dark:text-red-400">
					{error || "イベントが見つかりませんでした"}
				</p>
				<button
					onClick={() => navigate(eventPagePath)}
					className="mt-4 px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-medium transition-all"
				>
					イベントページに戻る
				</button>
			</div>
		);
	}

	return (
		<div className="max-w-4xl mx-auto space-y-6 pb-10">
			{/* Back Button */}
			<div className="pt-6">
				<button
					onClick={() => navigate(eventPagePath)}
					className="flex items-center text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white transition-colors"
				>
					<svg
						className="w-5 h-5 mr-1"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M15 19l-7-7 7-7"
						/>
					</svg>
					戻る
				</button>
			</div>

			{/* Progress Steps */}
			<div>
				<ul className="relative flex flex-row justify-center max-w-2xl mx-auto w-full pt-2 px-2">
					{([
						{ index: 1, label: "チケット確認", icon: FiClipboard },
						{ index: 2, label: "注文情報", icon: FiFileText },
						{ index: 3, label: "お支払い", icon: FiCreditCard },
					] as const).map((s) => {
						const IconComponent = s.icon;
						const isActive = s.index === step;
						const isCompleted = s.index < step;

						return (
							<li
								key={s.index}
								className="flex items-center gap-x-2 shrink group"
							>
								<div className="flex flex-col items-center">
									<div className="flex items-center">
										<div className="flex flex-col items-center flex-shrink-0">
											<div
												className={`relative flex justify-center items-center w-12 h-12 rounded-full font-medium transition-all duration-300 ${
													isActive
														? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg scale-110"
														: isCompleted
														? "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
														: "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
												}`}
											>
												{isCompleted ? (
													<svg
														xmlns="http://www.w3.org/2000/svg"
														width="24"
														height="24"
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														strokeWidth="3"
														strokeLinecap="round"
														strokeLinejoin="round"
														className="w-5 h-5"
													>
														<polyline points="20 6 9 17 4 12"></polyline>
													</svg>
												) : (
													<IconComponent className="w-5 h-5" />
												)}
												{isActive && (
													<div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 opacity-30 animate-ping"></div>
												)}
											</div>
											<span
												className={`mt-2 text-xs font-medium transition-colors whitespace-nowrap ${
													isActive || isCompleted
														? "text-blue-600 dark:text-blue-400"
														: "text-gray-600 dark:text-gray-400"
												}`}
											>
												{s.label}
											</span>
										</div>
										{s.index !== 3 && (
											<div className="w-12 sm:w-24 h-0.5 mx-1 sm:mx-2 mb-6 relative">
												<div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 rounded-full" />
												<div
													className={`absolute inset-0 rounded-full transition-all duration-500 ${
														isCompleted
															? "bg-gradient-to-r from-blue-500 to-blue-600 w-full"
															: "w-0"
													}`}
												/>
											</div>
										)}
									</div>
								</div>
							</li>
						);
					})}
				</ul>
			</div>

			{/* Step 1: Ticket Confirmation */}
			{step === 1 && (
				<section className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-transparent dark:border-gray-700">
					<div className="p-6 border-b dark:border-gray-700">
						<h2 className="text-xl font-bold text-gray-900 dark:text-white">
							チケット確認
						</h2>
					</div>

					<div className="p-6 space-y-6">
						<div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-xl">
							<h3 className="font-semibold text-gray-900 dark:text-white mb-2">
								{event.title}
							</h3>
							<p className="text-gray-600 dark:text-gray-400 text-sm">
								{event.date.replace(
									/(\d+)-0?(\d+)-0?(\d+)T?.*Z?/,
									"$1年$2月$3日"
								)}
							</p>
							<p className="text-gray-600 dark:text-gray-400 text-sm">
								{event.venue_name}
							</p>
						</div>

						<div className="space-y-3">
							{state.tickets.map(({ ticketId, quantity }) => {
								const ticketType = event.ticket_types.find(
									(t: TicketType) => t.id === ticketId
								);
								if (!ticketType) return null;

								return (
									<div
										key={ticketId}
										className="flex justify-between items-center p-4 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700"
									>
										<div>
											<h3 className="font-semibold text-gray-900 dark:text-white">
												{ticketType.name}
											</h3>
											{ticketType.description && (
												<p className="text-gray-500 dark:text-gray-400 text-sm">
													{ticketType.description}
												</p>
											)}
											<p className="text-gray-600 dark:text-gray-400 text-sm">
												{ticketType.is_free
													? "無料"
													: `¥${Math.round(
															ticketType.price
													  ).toLocaleString()}`}{" "}
												× {quantity}枚
											</p>
										</div>
										<p className="font-bold text-gray-900 dark:text-white">
											{ticketType.is_free
												? "無料"
												: `¥${(
														Math.round(ticketType.price) * quantity
												  ).toLocaleString()}`}
										</p>
									</div>
								);
							})}
						</div>
					</div>

					<div className="p-6 border-t dark:border-gray-700">
						<div className="flex justify-between items-center mb-6">
							<span className="font-semibold text-gray-900 dark:text-white">
								合計
							</span>
							<span className="text-2xl font-bold text-gray-900 dark:text-white">
								¥{totalAmount.toLocaleString()}
							</span>
						</div>

						<div className="flex justify-between gap-3">
							<button
								onClick={() => navigate(eventPagePath)}
								className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 font-medium bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
							>
								戻る
							</button>
							<button
								onClick={() => setStep(2)}
								className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-bold transition-all"
							>
								次へ
							</button>
						</div>
					</div>
				</section>
			)}

			{/* Step 2: Order Details */}
			{step === 2 && (
				<section className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-transparent dark:border-gray-700">
					<div className="p-6 border-b dark:border-gray-700">
						<h2 className="text-xl font-bold text-gray-900 dark:text-white">
							注文情報
						</h2>
					</div>

					<div className="p-6 space-y-6">
						<div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-xl">
							<h3 className="font-semibold mb-3 text-gray-900 dark:text-white">
								注文者情報
							</h3>
							<div className="space-y-3">
								<div>
									<p className="text-sm text-gray-600 dark:text-gray-400">
										氏名
									</p>
									<p className="font-medium text-gray-900 dark:text-white">
										{user?.name
											? `${user.name.firstName} ${user.name.lastName} `
											: "未設定"}
									</p>
								</div>
								<div>
									<p className="text-sm text-gray-600 dark:text-gray-400">
										メールアドレス
									</p>
									<p className="font-medium text-gray-900 dark:text-white">
										{user?.email}
									</p>
								</div>
								{user?.address && (
									<div>
										<p className="text-sm text-gray-600 dark:text-gray-400">
											住所
										</p>
										<p className="font-medium text-gray-900 dark:text-white">
											{[
												user.address.postal_code,
												user.address.prefecture,
												user.address.city,
												user.address.street_address,
												user.address.building_name,
											]
												.filter(Boolean)
												.join(" ")}
										</p>
									</div>
								)}
							</div>
						</div>

						<div>
							<h3 className="font-semibold mb-3 text-gray-900 dark:text-white">
								注意事項
							</h3>
							{cautionList.length > 0 ? (
								<ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
									{cautionList.map((caution) => (
										<li key={caution.id} className="flex gap-2">
											<span className="text-blue-500 dark:text-blue-300">・</span>
											<span>{caution.content}</span>
										</li>
									))}
								</ul>
							) : (
								<p className="text-sm text-gray-500 dark:text-gray-400">
									主催者からの特別な注意事項はありません。
								</p>
							)}
						</div>
					</div>

					<div className="p-6 border-t dark:border-gray-700">
						<div className="flex justify-between items-center mb-6">
							<span className="font-semibold text-gray-900 dark:text-white">
								合計
							</span>
							<span className="text-2xl font-bold text-gray-900 dark:text-white">
								¥{totalAmount.toLocaleString()}
							</span>
						</div>

						<div className="flex justify-between gap-3">
							<button
								onClick={() => setStep(1)}
								className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 font-medium bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								disabled={isProcessing}
							>
								戻る
							</button>
							<button
								onClick={createOrder}
								className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
								disabled={isProcessing}
							>
								{isProcessing ? "処理中..." : "注文を確定する"}
							</button>
						</div>
					</div>
				</section>
			)}

			{/* Step 3: Payment */}
			{step === 3 && clientSecret && (
				<section className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-transparent dark:border-gray-700">
					<div className="p-6 border-b dark:border-gray-700">
						<h2 className="text-xl font-bold text-gray-900 dark:text-white">
							お支払い
						</h2>
					</div>

					<div className="p-6">
						<Elements
							stripe={stripePromise}
							options={{
								clientSecret,
								locale: "ja",
								appearance: {
									theme: isDarkMode ? "night" : "stripe",
									labels: "floating",
									variables: {
										colorPrimary: "#3b82f6",
										colorBackground: isDarkMode ? "#374151" : "#ffffff",
										colorText: isDarkMode ? "#f3f4f6" : "#1f2937",
										colorDanger: "#ef4444",
										fontFamily: "system-ui, sans-serif",
										spacingUnit: "4px",
										borderRadius: "8px",
									},
								},
							}}
						>
							<StripePaymentForm
								clientSecret={clientSecret}
								orderId={orderId!}
								amount={totalAmount}
								onPaymentSuccess={moveToPaymentStatusPage}
								onPaymentPending={moveToPaymentStatusPage}
								onPaymentError={handlePaymentError}
							/>
						</Elements>

						<div className="mt-6 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-4 rounded-xl">
							<p>
								決済情報は安全に処理されます。当サイトがカード情報を保存することはありません。
							</p>
						</div>
					</div>
				</section>
			)}
		</div>
	);
};

export default CheckoutPage;
